/**
 * pages/status.ts — v4 cleanup: typed, minimal, no dead code
 */

import { detectLang, t } from "../i18n";
import type { Lang } from "../i18n/copy";

interface StatusPayload {
  overall: string;
  services: Array<{
    slug: string;
    name_en: string;
    name_zh: string;
    status: string;
    message_en: string;
    message_zh: string;
    updated_at: string;
  }>;
  incidents: Array<{
    id: number;
    title_en: string;
    title_zh: string;
    body_en: string;
    body_zh: string;
    severity: string;
    started_at: string;
    resolved_at: string | null;
  }>;
  generated_at: string;
}

let cache: StatusPayload | null = null;

function badgeClass(status: string): string {
  return `badge badge-${status}`;
}

function render(lang: Lang): void {
  if (!cache) return;
  const copy = t(lang);
  const overall = document.getElementById("status-overall");
  if (overall) {
    overall.innerHTML = `
      <span class="${badgeClass(cache.overall)}">${cache.overall}</span>
      <span class="muted mono">${copy.overall} · ${new Date(cache.generated_at).toISOString()}</span>
    `;
  }

  const services = document.getElementById("status-services");
  if (services) {
    services.replaceChildren(
      ...cache.services.map((s) => {
        const card = document.createElement("article");
        card.className = "status-card";
        card.innerHTML = `
          <header>
            <h3>${lang === "zh" ? s.name_zh : s.name_en}</h3>
            <span class="${badgeClass(s.status)}">${s.status}</span>
          </header>
          <p>${lang === "zh" ? s.message_zh : s.message_en}</p>
          <p class="mono muted">${s.slug} · ${s.updated_at}</p>
        `;
        return card;
      }),
    );
  }

  const incidents = document.getElementById("status-incidents");
  if (incidents) {
    const open = cache.incidents.filter((i) => !i.resolved_at);
    const list = open.length ? open : cache.incidents.slice(0, 3);
    if (!list.length) {
      incidents.innerHTML = `<p class="muted">${copy.noIncidents}</p>`;
    } else {
      incidents.replaceChildren(
        ...list.map((i) => {
          const el = document.createElement("article");
          el.className = "incident-card";
          el.innerHTML = `
            <header>
              <h3>${lang === "zh" ? i.title_zh : i.title_en}</h3>
              <span class="badge badge-${i.severity}">${i.severity}</span>
            </header>
            <p>${lang === "zh" ? i.body_zh : i.body_en}</p>
            <p class="mono muted">${i.started_at}${i.resolved_at ? ` → ${i.resolved_at}` : " · open"}</p>
          `;
          return el;
        }),
      );
    }
  }
}

export async function initStatusPage(): Promise<void> {
  if (document.body.dataset.page !== "status") return;

  try {
    const res = await fetch("/api/status", { headers: { Accept: "application/json" } });
    cache = (await res.json()) as StatusPayload;
  } catch {
    cache = {
      overall: "degraded",
      services: [],
      incidents: [],
      generated_at: new Date().toISOString(),
    };
  }

  render(detectLang());
  document.addEventListener("doorpost:lang", ((e: CustomEvent<{ lang: Lang }>) => {
    render(e.detail.lang);
  }) as EventListener);
}
